import { motion } from "framer-motion";
import { ArrowRight, Terminal, Circle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fadeUp, stagger } from "@/lib/motion";

const go = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CODE_LINES = [
  { t: "const labs = new LiberoX({", c: "var(--lx-text)" },
  { t: "  focus: 'Apps, Web-Apps & Plattformen',", c: "var(--lx-text-2)" },
  { t: "  platforms: ['iOS', 'Android', 'Web'],", c: "var(--lx-text-2)" },
  { t: "  pricing: 'transparente Fixpreise',", c: "var(--lx-text-2)" },
  { t: "  warranty: '100% Geld-zur\u00fcck',", c: "var(--lx-text-2)" },
  { t: "});", c: "var(--lx-text)" },
  { t: "", c: "var(--lx-text)" },
  { t: "await labs.build(deineIdee);", c: "var(--lx-green-soft)" },
  { t: "// > inkl. laufender Updates \u2714", c: "var(--lx-muted)" },
];

export const Hero = () => {
  return (
    <section
      id="start"
      data-testid="hero-section"
      className="lx-noise relative overflow-hidden"
    >
      <div className="lx-hero-ambient absolute inset-0 -z-10" />

      <div className="mx-auto grid max-w-6xl grid-cols-12 gap-8 px-4 pb-16 pt-28 sm:px-6 sm:pb-20 sm:pt-32 lg:px-8 lg:pb-28 lg:pt-40">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="col-span-12 lg:col-span-7"
        >
          <motion.div
            variants={fadeUp}
            className="mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5"
            style={{
              borderColor: "rgba(168,232,92,0.22)",
              background: "rgba(168,232,92,0.07)",
            }}
          >
            <Terminal size={13} style={{ color: "var(--lx-green)" }} />
            <span
              className="font-mono-lx text-[11px] uppercase tracking-[0.22em]"
              style={{ color: "var(--lx-green-soft)" }}
            >
              build // ship // scale
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[color:var(--lx-text)] sm:text-5xl lg:text-6xl"
          >
            Die nächste App beginnt{" "}
            <span style={{ color: "var(--lx-green)" }}>hier</span>.
            <span className="lx-caret" aria-hidden="true" />
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--lx-text-2)] sm:text-lg"
          >
            Von der ersten Idee bis zur Veröffentlichung entwickeln wir
            individuelle Mobile- und Web-Apps mit modernster Technologie,
            intelligenter KI-Integration, intuitivem Design und skalierbarer
            Architektur. Das Ergebnis sind leistungsstarke digitale Produkte, die
            echten Mehrwert schaffen und mit Ihrem Unternehmen wachsen.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Button
              data-testid="hero-primary-cta"
              onClick={() => go("#kontakt")}
              className="group h-12 rounded-[12px] px-6 font-display font-medium"
              style={{
                background: "var(--lx-green)",
                color: "var(--lx-canvas)",
                boxShadow: "0 10px 30px rgba(168,232,92,0.16)",
              }}
            >
              Projekt anfragen
              <ArrowRight
                size={18}
                className="ml-2 transition-transform group-hover:translate-x-1"
              />
            </Button>
            <Button
              data-testid="hero-secondary-cta"
              onClick={() => go("#projekte")}
              variant="outline"
              className="h-12 rounded-[12px] border px-6 font-display font-medium"
              style={{
                borderColor: "var(--lx-border-strong)",
                background: "transparent",
                color: "var(--lx-text)",
              }}
            >
              Projekte ansehen
            </Button>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-5 flex items-center gap-2.5"
          >
            <ShieldCheck size={16} style={{ color: "var(--lx-green)" }} />
            <span className="text-sm text-[color:var(--lx-text-2)]">
              <span style={{ color: "var(--lx-text)" }}>100% Geld-zurück-Garantie</span>{" "}
              · Transparente Fixpreise
            </span>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3"
          >
            {["React", "React Native", "FastAPI", "MongoDB", "Swift"].map(
              (tech) => (
                <span
                  key={tech}
                  className="font-mono-lx text-xs"
                  style={{ color: "var(--lx-muted)" }}
                >
                  <span style={{ color: "var(--lx-green-dim)" }}>#</span>
                  {tech}
                </span>
              )
            )}
          </motion.div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="col-span-12 lg:col-span-5"
        >
          <div className="lx-terminal mx-auto max-w-md">
            <div className="lx-terminal-bar">
              <div className="flex items-center gap-2">
                <span
                  className="lx-dot"
                  style={{ background: "#ff5f56" }}
                />
                <span
                  className="lx-dot"
                  style={{ background: "#ffbd2e" }}
                />
                <span
                  className="lx-dot"
                  style={{ background: "#27c93f" }}
                />
              </div>
              <span
                className="font-mono-lx text-[11px] uppercase tracking-[0.18em]"
                style={{ color: "var(--lx-muted)" }}
              >
                liberox — labs.js
              </span>
              <span className="w-10" />
            </div>

            <div className="p-5">
              <pre className="font-mono-lx text-[12.5px] leading-relaxed sm:text-[13px]">
                {CODE_LINES.map((line, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35 + i * 0.12, duration: 0.3 }}
                    style={{ color: line.c, minHeight: "1.4em" }}
                  >
                    <span
                      className="mr-3 select-none"
                      style={{ color: "rgba(127,146,135,0.55)" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {line.t}
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.4 }}
                  style={{ color: "var(--lx-green)" }}
                >
                  <span
                    className="mr-3 select-none"
                    style={{ color: "rgba(127,146,135,0.55)" }}
                  >
                    {String(CODE_LINES.length + 1).padStart(2, "0")}
                  </span>
                  ${" "}
                  <span className="lx-caret" aria-hidden="true" />
                </motion.div>
              </pre>
            </div>

            <div
              className="flex items-center justify-between border-t px-5 py-3"
              style={{ borderColor: "var(--lx-border)" }}
            >
              <span className="flex items-center gap-2 font-mono-lx text-[11px]" style={{ color: "var(--lx-text-2)" }}>
                <Circle size={8} fill="#27c93f" stroke="none" />
                build passed
              </span>
              <span className="font-mono-lx text-[11px]" style={{ color: "var(--lx-muted)" }}>
                seit 2022 · Schweiz
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
